import { getRuntimeDocument } from '@imagise/shared';
import type { CanvasSize, CSSDimensions, ElementOffset } from './util';
import {
  CANVAS_DATA_ATTRIBUTE,
  getElementOffset,
  setCanvasDimensions,
  setCSSDimensions,
} from './util';

const getDomDocument = (): Document | undefined => {
  try {
    return getRuntimeDocument();
  } catch {
    return undefined;
  }
};

export type CanvasItem = {
  el: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D | null;
};

export class StaticCanvasDOMManager {
  /**
   * Keeps a copy of the canvas style before setting retina scaling and other options
   * in order to return it to original state on dispose.
   */
  private _originalCanvasStyle?: string;

  lower: CanvasItem;

  constructor(arg0?: string | HTMLCanvasElement) {
    const el = this.createLowerCanvas(arg0);
    const ctx = typeof el.getContext === 'function' ? el.getContext('2d') : null;
    this.lower = { el, ctx };
  }

  protected createLowerCanvas(
    arg0?: HTMLCanvasElement | string,
  ): HTMLCanvasElement {
    const el = this.resolveLowerCanvas(arg0);
    if (
      el.hasAttribute?.(CANVAS_DATA_ATTRIBUTE) ||
      el.hasAttribute?.('data-static-canvas')
    ) {
      throw new Error(
        'Trying to initialize a canvas that has already been initialized. ' +
          'Did you forget to dispose the canvas?',
      );
    }
    this._originalCanvasStyle = el.style?.cssText;
    el.setAttribute?.(CANVAS_DATA_ATTRIBUTE, 'main');
    el.classList?.add('lower-canvas');
    return el;
  }

  cleanupDOM({ width, height }: CanvasSize): void {
    const { el } = this.lower;
    // restore canvas style and attributes
    el.classList?.remove('lower-canvas');
    el.removeAttribute?.(CANVAS_DATA_ATTRIBUTE);
    // restore canvas size to original size in case retina scaling was applied
    el.setAttribute?.('width', `${width}`);
    el.setAttribute?.('height', `${height}`);
    if (el.style) {
      el.style.cssText = this._originalCanvasStyle || '';
    }
    this._originalCanvasStyle = undefined;
  }

  setDimensions(size: CanvasSize, retinaScaling: number): void {
    const { el, ctx } = this.lower;
    setCanvasDimensions(el, ctx, size, retinaScaling);
  }

  setCSSDimensions(size: Partial<CSSDimensions>): void {
    setCSSDimensions(this.lower.el, size);
  }

  /**
   * Calculates canvas element offset relative to the document.
   */
  calcOffset(): ElementOffset {
    return getElementOffset(this.lower.el);
  }

  dispose(): void {
    this._originalCanvasStyle = undefined;
    // @ts-expect-error disposing
    delete this.lower;
  }

  private resolveLowerCanvas(
    arg0?: HTMLCanvasElement | string,
  ): HTMLCanvasElement {
    if (this.isHTMLCanvas(arg0)) {
      return arg0;
    }
    if (typeof arg0 === 'string') {
      const found = getDomDocument()?.getElementById(arg0) as HTMLCanvasElement | null;
      if (found) {
        return found;
      }
    }
    return this.createCanvasElement();
  }

  protected createCanvasElement(): HTMLCanvasElement {
    const runtimeDocument = getDomDocument();
    if (runtimeDocument && typeof runtimeDocument.createElement === 'function') {
      return runtimeDocument.createElement('canvas') as HTMLCanvasElement;
    }
    return {} as HTMLCanvasElement;
  }

  private isHTMLCanvas(
    canvas?: HTMLCanvasElement | string,
  ): canvas is HTMLCanvasElement {
    return (
      !!canvas && typeof (canvas as HTMLCanvasElement).getContext === 'function'
    );
  }
}
